/**
 * Hydrating a prerendered page (scripts/prerender.mjs).
 *
 * A prerendered page is its first render for a signed-out visitor reading its language: English
 * at the root addresses, Turkish at `/tr/…` (`PAGE_LOCALES`). The prerender stamps that on
 * `<html>`: `data-prerender` (the {@link renderVariant}) and `data-prerender-path`. A visitor
 * whose first render matches is hydrated; anyone else (another language, signed in, another
 * path) starts from an empty root, as before prerendering. The boot script in index.html
 * predicts the same before first paint and hides a page that is about to be replaced
 * (`data-prerender-hidden`), so it never flashes in another language; a `/tr/` page adds
 * `localeBootScript()` (seo/head.ts), since its language comes from the address.
 */

import { i18n, normalizeLocale } from '@/i18n';
import { useAuthStore } from '@/stores/authStore';

/** What a public page's first render depends on besides its path. index.html predicts it too. */
export function renderVariant(): string {
  const locale = normalizeLocale(i18n.resolvedLanguage ?? i18n.language);
  const signedIn = useAuthStore.getState().isAuthenticated;
  return `${locale}|${signedIn ? 'in' : 'out'}`;
}

/** A path without its trailing slash: nginx.conf serves `/tr` and `/tr/` the same page. */
function trimSlash(path: string): string {
  return path.replace(/\/+$/, '') || '/';
}

/** True when `root` holds a prerendered page that this visitor's first render reproduces. */
export function canHydrate(root: HTMLElement): boolean {
  const { prerender, prerenderPath } = document.documentElement.dataset;
  return (
    Boolean(prerender) &&
    root.hasChildNodes() &&
    trimSlash(prerenderPath ?? '') === trimSlash(window.location.pathname) &&
    prerender === renderVariant()
  );
}

/** Undo the boot script's hiding, once the root holds what this visitor should see. */
export function revealPrerender(): void {
  delete document.documentElement.dataset.prerenderHidden;
}
